/**
 * Pick an animal and colour it.
 *
 * Locked animals show as silhouettes with the two ways to earn them. The colour
 * swatches tint the body layer only, so eyes and beaks stay readable (CLAUDE.md).
 */
import Phaser from 'phaser';
import { setDevState } from '../systems/devQuery';
import { drawVerticalGradient, MENU_GRADIENT } from '../art/backdrop';
import { AnimalSprite } from '../art/AnimalSprite';
import { artScale } from '../art/artScale';
import { IconButton } from './ui/IconButton';
import {
  ANIMALS,
  animalFrameKey,
  bodyTintFor,
  COLOR_SWATCHES,
  isAnimalUnlocked,
  unlockHintFor,
} from '../data/animals';
import type { AnimalDef, AnimalId } from '../data/animals';
import { COSMETIC_MILESTONES } from '../systems/TideRun';
import { ensurePlaceholderTexture } from '../art/placeholders';
import type { SaveManager } from '../systems/SaveManager';
import { FONT_FAMILY, GAME_HEIGHT, GAME_WIDTH, IS_PORTRAIT, PALETTE } from '../types';

// Wide stage: 6 x 2. Tall stage: 3 x 4, with the preview and colours below.
const GRID_COLS = IS_PORTRAIT ? 3 : 6;
const CELL_W = IS_PORTRAIT ? 224 : 192;
const CELL_H = IS_PORTRAIT ? 150 : 148;
/** Cells are drawn from GRID_TOP - CELL_TOP_OFFSET, so this clears the header. */
const GRID_TOP = IS_PORTRAIT ? 255 : 202;
const CELL_TOP_OFFSET = 64;
const SWATCH_Y = IS_PORTRAIT ? GAME_HEIGHT - 200 : 656;
/** Tall stage: the preview panel's top, between the grid and the colours. */
const PORTRAIT_PREVIEW_Y = SWATCH_Y - 280;

/** Centre of the grid cell for animal `index`. */
function cellPos(index: number): { x: number; y: number } {
  const col = index % GRID_COLS;
  const row = Math.floor(index / GRID_COLS);
  return {
    x: GAME_WIDTH / 2 + (col - (GRID_COLS - 1) / 2) * CELL_W,
    y: GRID_TOP + row * CELL_H,
  };
}

/** Centre of colour swatch `i` (0 is Natural): one row, or two when tall. */
function swatchPos(i: number): { x: number; y: number } {
  const total = COLOR_SWATCHES.length + 1;
  if (!IS_PORTRAIT) {
    const spacing = Math.min(86, (GAME_WIDTH - 100) / (total - 1));
    return { x: GAME_WIDTH / 2 - ((total - 1) * spacing) / 2 + i * spacing, y: SWATCH_Y };
  }
  const perRow = Math.ceil(total / 2);
  const row = Math.floor(i / perRow);
  const inRow = row === 0 ? perRow : total - perRow;
  const col = i % perRow;
  const spacing = Math.min(104, (GAME_WIDTH - 90) / (perRow - 1));
  return {
    x: GAME_WIDTH / 2 + (col - (inRow - 1) / 2) * spacing,
    y: SWATCH_Y + row * 110,
  };
}

export class AnimalSelect extends Phaser.Scene {
  private save!: SaveManager;
  private levelsCompleted = 0;
  private totalStars = 0;
  private selectedId: AnimalId = 'emperor-penguin';
  private selectedIndex = 0;
  private cells: Phaser.GameObjects.Graphics[] = [];
  /** Each cell's body layer, retinted when its colour changes. */
  private icons: Phaser.GameObjects.Image[] = [];
  private preview: AnimalSprite | undefined;
  private previewName!: Phaser.GameObjects.Text;
  private previewBlurb!: Phaser.GameObjects.Text;
  private swatchMarkers: Phaser.GameObjects.Graphics[] = [];
  /**
   * Which part of the screen the arrow keys are driving. The colour row used
   * to be mouse-only - the arrows only moved round the grid - so a child on
   * the keyboard could choose an animal but never recolour it.
   */
  private zone: 'grid' | 'swatches' = 'grid';
  /** Keyboard focus in the colour row; 0 is "natural". */
  private swatchFocus = 0;
  /**
   * The cell under the mouse. Hover only highlights: it must never move focus,
   * because focus is what the colour row recolours, and the mouse crosses
   * other animals on its way down to the colours.
   */
  private hoverIndex: number | null = null;

  constructor() {
    super('AnimalSelect');
  }

  create(): void {
    setDevState({ scene: 'AnimalSelect' });
    // Scene instances are reused, so clear anything that accumulates.
    this.cells.length = 0;
    this.icons.length = 0;
    this.swatchMarkers.length = 0;
    this.preview = undefined;
    this.zone = 'grid';
    this.swatchFocus = 0;
    this.save = this.registry.get('save') as SaveManager;
    // Grant any Tide Run reward already earned but not yet given.
    this.save.refreshTideRewards();
    this.levelsCompleted = this.save.levelsCompleted();
    // Tide stars count toward animals too (docs/TIDE_RUN.md).
    this.totalStars = this.save.unlockStars();
    this.selectedId = this.save.activeProfile()?.selectedAnimal ?? 'emperor-penguin';
    this.selectedIndex = Math.max(
      0,
      ANIMALS.findIndex((animal) => animal.id === this.selectedId),
    );

    this.cameras.main.setBackgroundColor(PALETTE.sky);
    this.drawBackdrop();

    // Back to the map, the same as Esc, for anyone using the mouse.
    new IconButton(this, 56, 56, 'back', () => this.scene.start('LevelMap'), 52);

    this.add
      .text(GAME_WIDTH / 2, 56, 'Choose your climber', {
        fontFamily: FONT_FAMILY,
        fontSize: '48px',
        color: PALETTE.snow,
        stroke: PALETTE.ink,
        strokeThickness: 8,
      })
      .setOrigin(0.5);

    this.add
      .text(GAME_WIDTH / 2, 100, `${this.totalStars} stars      ${this.levelsCompleted} levels`, {
        fontFamily: FONT_FAMILY,
        fontSize: '20px',
        color: PALETTE.gold,
      })
      .setOrigin(0.5);

    this.add
      .text(
        GAME_WIDTH / 2,
        124,
        IS_PORTRAIT
          ? 'Tap an animal to pick it, then a colour.'
          : 'Click or use the arrows to pick.  Tab for colours.  Esc goes back.',
        {
          fontFamily: FONT_FAMILY,
          fontSize: '16px',
          color: PALETTE.ice,
        },
      )
      .setOrigin(0.5);

    this.drawGrid();
    this.drawPreviewPanel();
    this.drawSwatches();
    this.installInput();
    this.refresh();
  }

  /* ------------------------------------------------------------------ grid */

  private drawGrid(): void {
    ANIMALS.forEach((animal, index) => {
      const { x, y } = cellPos(index);
      const unlocked = this.isUnlocked(animal);

      const cell = this.add.graphics();
      this.cells.push(cell);

      // A silhouette for locked animals; body plus details for unlocked ones.
      const body = this.add
        .image(x, y + 26, animalFrameKey(animal.id, 'idle', 0, 'body'))
        .setOrigin(0.5, 1)
        .setScale(0.42 * artScale(this, animalFrameKey(animal.id, 'idle', 0, 'body')));
      this.icons.push(body);
      const details = this.add
        .image(x, y + 26, animalFrameKey(animal.id, 'idle', 0, 'details'))
        .setOrigin(0.5, 1)
        .setScale(0.42 * artScale(this, animalFrameKey(animal.id, 'idle', 0, 'details')));

      if (unlocked) {
        body.setTint(bodyTintFor(animal, this.save.activeProfile()?.colorPerAnimal[animal.id]));
      } else {
        body.setTint(0x101a2c);
        details.setTint(0x101a2c).setAlpha(0.55);
      }

      this.add
        .text(x, y + 32, unlocked ? animal.name : 'Locked', {
          fontFamily: FONT_FAMILY,
          fontSize: '16px',
          color: unlocked ? PALETTE.snow : PALETTE.ice,
          align: 'center',
          wordWrap: { width: CELL_W - 28 },
        })
        .setOrigin(0.5, 0);

      if (!unlocked) {
        // Inside the cell, so it cannot collide with the row below.
        this.add
          .text(x, y + 52, unlockHintFor(animal), {
            fontFamily: FONT_FAMILY,
            fontSize: '12px',
            color: PALETTE.sunset,
            align: 'center',
            wordWrap: { width: CELL_W - 30 },
          })
          .setOrigin(0.5, 0);
      }

      const hit = this.add
        .rectangle(x, y - 6, CELL_W - 16, CELL_H - 16, 0xffffff, 0)
        .setInteractive({ useHandCursor: unlocked });
      hit.on('pointerover', () => this.setHover(index));
      hit.on('pointerout', () => this.setHover(null));
      // A click looks at the animal (locked ones too, to read how to unlock
      // them) and picks it if it can be picked.
      hit.on('pointerup', () => {
        this.focus(index);
        this.choose(index);
      });
    });
  }

  private drawPreviewPanel(): void {
    const panelY = IS_PORTRAIT ? PORTRAIT_PREVIEW_Y : GAME_HEIGHT - 258;
    const half = Math.min(420, GAME_WIDTH / 2 - 24);
    const panel = this.add.graphics();
    panel.fillStyle(0x16233a, 0.92);
    panel.fillRoundedRect(GAME_WIDTH / 2 - half, panelY, half * 2, 170, 22);
    panel.lineStyle(4, 0x8ecae6, 0.9);
    panel.strokeRoundedRect(GAME_WIDTH / 2 - half, panelY, half * 2, 170, 22);

    this.previewName = this.add
      .text(GAME_WIDTH / 2 + (IS_PORTRAIT ? 70 : 40), panelY + 34, '', {
        fontFamily: FONT_FAMILY,
        fontSize: '32px',
        color: PALETTE.snow,
      })
      .setOrigin(0.5, 0);

    this.previewBlurb = this.add
      .text(GAME_WIDTH / 2 + (IS_PORTRAIT ? 70 : 40), panelY + 80, '', {
        fontFamily: FONT_FAMILY,
        fontSize: '19px',
        color: PALETTE.ice,
        align: 'center',
        wordWrap: { width: IS_PORTRAIT ? 420 : 520 },
      })
      .setOrigin(0.5, 0);
  }

  private drawSwatches(): void {
    ensurePlaceholderTexture(this, 'ui/lock');
    const total = COLOR_SWATCHES.length + 1;

    // Index 0 is "natural"; the rest map to COLOR_SWATCHES.
    for (let i = 0; i < total; i += 1) {
      const { x, y } = swatchPos(i);
      const swatch = i === 0 ? null : COLOR_SWATCHES[i - 1];

      const dot = this.add.graphics();
      dot.fillStyle(swatch?.value ?? 0xf2f2f2, 1);
      dot.fillCircle(x, y, 22);
      dot.lineStyle(3, 0x0b1220, 1);
      dot.strokeCircle(x, y, 22);
      if (swatch === null) {
        dot.lineStyle(3, 0x1b2a41, 1);
        dot.lineBetween(x - 13, y + 13, x + 13, y - 13);
      }
      // A Tide Run colour not yet earned: dimmed, with a lock over it.
      if (swatch !== null && !this.isSwatchOpen(i - 1)) {
        dot.setAlpha(0.45);
        this.add.image(x, y, 'ui/lock').setDisplaySize(22, 22).setAlpha(0.9);
      }

      const marker = this.add.graphics();
      this.swatchMarkers.push(marker);

      this.add
        .text(x, y + 30, swatch?.name ?? 'Natural', {
          fontFamily: FONT_FAMILY,
          fontSize: '13px',
          color: PALETTE.ice,
        })
        .setOrigin(0.5, 0);

      const hit = this.add.circle(x, y, 26, 0xffffff, 0).setInteractive({ useHandCursor: true });
      hit.on('pointerover', () => this.focusSwatch(i));
      hit.on('pointerup', () => this.applyColor(i === 0 ? null : i - 1));
    }
  }

  /* ----------------------------------------------------------------- input */

  private installInput(): void {
    this.input.keyboard?.on('keydown', (event: KeyboardEvent) => {
      if (this.zone === 'swatches') {
        this.onSwatchKey(event);
        return;
      }
      if (event.key === 'Escape') {
        this.scene.start('LevelMap');
        return;
      }
      const bottomRow = this.selectedIndex >= ANIMALS.length - GRID_COLS;
      const focused = ANIMALS[this.selectedIndex];
      // Tab reaches the colours from any row. Down only does it from the
      // bottom row, because from the top it has to move to the cell below -
      // and the animals most players own (the two penguins) are in the top
      // row, so Down alone left them impossible to recolour by keyboard.
      const toColours = event.key === 'Tab' || (event.key === 'ArrowDown' && bottomRow);
      if (toColours && focused !== undefined && this.isUnlocked(focused)) {
        event.preventDefault();
        const current = this.save.activeProfile()?.colorPerAnimal[focused.id];
        this.focusSwatch(current === undefined ? 0 : current + 1);
        return;
      }
      if (event.key === 'Tab') {
        event.preventDefault();
        return;
      }
      if (event.key === 'ArrowRight') this.focus((this.selectedIndex + 1) % ANIMALS.length);
      else if (event.key === 'ArrowLeft') {
        this.focus((this.selectedIndex + ANIMALS.length - 1) % ANIMALS.length);
      } else if (event.key === 'ArrowDown') {
        this.focus((this.selectedIndex + GRID_COLS) % ANIMALS.length);
      } else if (event.key === 'ArrowUp') {
        this.focus((this.selectedIndex + ANIMALS.length - GRID_COLS) % ANIMALS.length);
      } else if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        this.choose(this.selectedIndex);
      }
    });
  }

  private setHover(index: number | null): void {
    if (index === this.hoverIndex) return;
    this.hoverIndex = index;
    this.drawCells();
  }

  private focus(index: number): void {
    this.zone = 'grid';
    this.selectedIndex = index;
    this.refresh();
  }

  private onSwatchKey(event: KeyboardEvent): void {
    const total = COLOR_SWATCHES.length + 1;
    if (event.key === 'ArrowLeft') this.focusSwatch((this.swatchFocus + total - 1) % total);
    else if (event.key === 'ArrowRight') this.focusSwatch((this.swatchFocus + 1) % total);
    else if (event.key === 'ArrowUp' || event.key === 'Escape' || event.key === 'Tab') {
      // Back up to the grid; Esc only leaves the screen from there.
      this.focus(this.selectedIndex);
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.applyColor(this.swatchFocus === 0 ? null : this.swatchFocus - 1);
    }
    if (event.key === 'Tab') event.preventDefault();
  }

  private focusSwatch(index: number): void {
    // A locked animal cannot be recoloured, and its colour row draws no rings,
    // so focus there would be invisible and do nothing.
    const focused = ANIMALS[this.selectedIndex];
    if (focused === undefined || !this.isUnlocked(focused)) return;
    this.zone = 'swatches';
    this.swatchFocus = index;
    this.refresh();
  }

  private choose(index: number): void {
    const animal = ANIMALS[index];
    if (animal === undefined || !this.isUnlocked(animal)) return;
    this.selectedId = animal.id;
    this.selectedIndex = index;
    this.save.setSelectedAnimal(animal.id);
    this.refresh();
  }

  /** Every swatch is open except the Tide Run ones the player hasn't earned. */
  private isSwatchOpen(swatchIndex: number): boolean {
    const cosmetic = COLOR_SWATCHES[swatchIndex]?.cosmetic;
    return cosmetic === undefined || this.save.hasCosmetic(cosmetic);
  }

  private applyColor(swatchIndex: number | null): void {
    const animal = ANIMALS[this.selectedIndex];
    if (animal === undefined || !this.isUnlocked(animal)) return;
    if (swatchIndex !== null && !this.isSwatchOpen(swatchIndex)) {
      // Say how to earn it, where the animal's blurb usually is.
      const cosmetic = COLOR_SWATCHES[swatchIndex]?.cosmetic;
      const milestone = COSMETIC_MILESTONES.find((m) => m.id === cosmetic);
      if (milestone !== undefined) {
        const have = Math.min(milestone.need, milestone.have(this.save.tideProgress()));
        this.previewBlurb.setText(
          `${milestone.name}: ${milestone.hint}. So far: ${have} of ${milestone.need}.`,
        );
      }
      return;
    }
    this.save.setAnimalColor(animal.id, swatchIndex);
    this.icons[this.selectedIndex]?.setTint(bodyTintFor(animal, swatchIndex ?? undefined));
    this.refresh();
  }

  /* ---------------------------------------------------------------- render */

  private refresh(): void {
    const focused = ANIMALS[this.selectedIndex];
    if (focused === undefined) return;
    const unlocked = this.isUnlocked(focused);

    this.drawCells();

    this.previewName.setText(unlocked ? focused.name : `${focused.name} - locked`);
    this.previewBlurb.setText(unlocked ? focused.blurb : unlockHintFor(focused));

    const colorIndex = this.save.activeProfile()?.colorPerAnimal[focused.id];
    this.preview?.destroy();
    this.preview = new AnimalSprite(
      this,
      IS_PORTRAIT ? GAME_WIDTH / 2 - 250 : GAME_WIDTH / 2 - 330,
      IS_PORTRAIT ? PORTRAIT_PREVIEW_Y + 140 : GAME_HEIGHT - 120,
      focused.id,
      unlocked ? bodyTintFor(focused, colorIndex) : 0x101a2c,
    );
    this.preview.setDisplayScale(0.78);

    this.swatchMarkers.forEach((marker, i) => {
      marker.clear();
      if (!unlocked) return;
      const { x, y } = swatchPos(i);
      const isCurrent = i === 0 ? colorIndex === undefined : colorIndex === i - 1;
      if (isCurrent) {
        marker.lineStyle(4, 0xffc145, 1);
        marker.strokeCircle(x, y, 28);
      }
      // Keyboard focus: a wider snow ring outside the gold one, so "where I
      // am" and "what is chosen" can both be seen when they differ.
      if (this.zone === 'swatches' && i === this.swatchFocus) {
        marker.lineStyle(3, 0xf7fbff, 1);
        marker.strokeCircle(x, y, 34);
      }
    });
  }

  /** Highlight the focused cell, ring the one in use, and outline the hovered one. */
  private drawCells(): void {
    this.cells.forEach((cell, index) => {
      if (!cell.active) return;
      const animal = ANIMALS[index];
      const { x, y } = cellPos(index);

      cell.clear();
      const isFocused = index === this.selectedIndex;
      const isHover = index === this.hoverIndex && !isFocused;
      const inUse = animal?.id === this.selectedId;
      cell.fillStyle(isFocused ? 0x1d3557 : isHover ? 0x16243d : 0x101a2c, isFocused ? 0.95 : 0.8);
      cell.fillRoundedRect(x - CELL_W / 2 + 8, y - CELL_TOP_OFFSET, CELL_W - 16, CELL_H - 16, 16);
      const edge = inUse ? 0x2ec4b6 : isFocused ? 0xffc145 : isHover ? 0x8ecae6 : 0x44546b;
      cell.lineStyle(inUse ? 5 : 3, edge, 1);
      cell.strokeRoundedRect(x - CELL_W / 2 + 8, y - CELL_TOP_OFFSET, CELL_W - 16, CELL_H - 16, 16);
    });
  }

  private isUnlocked(animal: AnimalDef): boolean {
    return isAnimalUnlocked(animal, this.levelsCompleted, this.totalStars);
  }

  private drawBackdrop(): void {
    drawVerticalGradient(this, MENU_GRADIENT);
  }
}
